import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';

import { useDecodedToken } from '../app/hooks';
import { useGetTrainingPhotosQuery, useUploadTrainingPhotoMutation, useDeleteTrainingPhotoMutation } from '../api/apiSlice';

interface TrainingPhotosDialogProps {
  trainingID: number;
  onClose: () => void;
}

const TrainingPhotosDialog = ({ trainingID, onClose }: TrainingPhotosDialogProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const decodedToken = useDecodedToken();
  const userID = decodedToken?.id ?? -1;
  const role = decodedToken?.role ?? null;
  const canModerate = role === 'ADMIN' || role === 'ANIMATOR';

  const { data: photos = [], isLoading } = useGetTrainingPhotosQuery(trainingID);
  const [uploadPhoto, { isLoading: isUploading }] = useUploadTrainingPhotoMutation();
  const [deletePhoto] = useDeleteTrainingPhotoMutation();
  const [error, setError] = useState(false);

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const formData = new FormData();
    formData.append('photo', file);

    setError(false);
    try {
      await uploadPhoto({ trainingID, formData }).unwrap();
    } catch (err) {
      console.error('error', err);
      setError(true);
    }
  };

  const handleDelete = async (photoID: number) => {
    try {
      await deletePhoto({ trainingID, photoID }).unwrap();
    } catch (err) {
      console.error('error', err);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('trainingPhotos.title')}</DialogTitle>
      <DialogContent>
        {error && (
          <Box mt={1} mb={1}>
            <Alert severity="error">{t('common.genericError')}</Alert>
          </Box>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : photos.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
            {t('trainingPhotos.empty')}
          </Typography>
        ) : (
          <Grid container spacing={1} sx={{ mt: 0 }}>
            {photos.map((photo) => (
              <Grid item key={photo.ID} xs={6} sm={4}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={photo.URL}
                    alt={t('activities.by', { name: `${photo.FIRSTNAME} ${photo.NAME}` })}
                    sx={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 1 }}
                  />
                  {(canModerate || photo.UPLOADED_BY === userID) && (
                    <IconButton
                      size="small"
                      aria-label={t('trainingPhotos.deleteAction')}
                      onClick={() => handleDelete(photo.ID)}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        bgcolor: 'rgba(255,255,255,0.8)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {photo.FIRSTNAME}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={handleFileSelected}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <AddPhotoAlternateIcon />}
        >
          {t('trainingPhotos.addPhoto')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrainingPhotosDialog;
